/**
 * Export service for PDF and Excel generation
 */

import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { formatCurrency, formatPercent } from './mortgageCalculations';

/**
 * Export loan summary and amortization schedule to PDF
 * @param {object} loanData - Complete loan data
 * @param {object} calculatedData - Calculated results
 * @param {object} prepaymentData - Prepayment configuration
 * @param {array} comparisons - List of loan comparisons
 * @param {object} manualOverrides - Manual override values
 */
export function exportToPDF(loanData, calculatedData, prepaymentData, comparisons, manualOverrides) {
    try {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;

        // Helper to add section title
        const addSectionTitle = (title, y) => {
            doc.setFontSize(14);
            doc.setTextColor(0, 0, 0);
            doc.text(title, 14, y);
            return y + 10;
        };

        // Title
        doc.setFontSize(20);
        doc.setTextColor(102, 126, 234);
        doc.text('Principal Saver - Loan Analysis', 14, 20);
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 26);

        // Loan Summary
        let currentY = 35;
        currentY = addSectionTitle('Loan Summary', currentY);

        doc.setFontSize(10);
        const summaryData = [
            ['Home Price', formatCurrency(loanData.homePrice)],
            ['Down Payment', `${formatCurrency(loanData.downPayment)} (${loanData.downPaymentPercent}%)`],
            ['Loan Amount', formatCurrency(calculatedData.loanAmount)],
            ['Interest Rate', formatPercent(loanData.interestRate)],
            ['Loan Type', loanData.loanType],
            ['Buydown Option', loanData.buydownType || 'None'],
            ['Zip Code', loanData.zipCode]
        ];

        doc.autoTable({
            startY: currentY,
            head: [['Item', 'Value']],
            body: summaryData,
            theme: 'grid',
            headStyles: { fillColor: [102, 126, 234] }
        });

        // Monthly Payment Breakdown
        currentY = doc.lastAutoTable.finalY + 15;
        currentY = addSectionTitle('Monthly Payment Breakdown', currentY);

        const paymentData = [
            ['Principal & Interest', formatCurrency(calculatedData.monthlyPI)],
            ['Property Tax', formatCurrency(calculatedData.escrowDetails.propertyTaxDetails.amount) + (manualOverrides.propertyTax ? ' (Manual)' : '')],
            ['Homeowner Insurance', formatCurrency(calculatedData.escrowDetails.insuranceDetails.amount) + (manualOverrides.insurance ? ' (Manual)' : '')],
            ['PMI', formatCurrency(calculatedData.autoValues.pmi) + (manualOverrides.pmi ? ' (Manual)' : '')],
            ['Total Escrow', formatCurrency(calculatedData.escrow)],
            ['Total Monthly Payment', formatCurrency(calculatedData.totalMonthly)]
        ];

        doc.autoTable({
            startY: currentY,
            head: [['Component', 'Amount']],
            body: paymentData,
            theme: 'grid',
            headStyles: { fillColor: [102, 126, 234] }
        });

        // Prepayment Information (if active)
        if (prepaymentData && prepaymentData.extraPayment > 0) {
            currentY = doc.lastAutoTable.finalY + 15;

            // Check if we need a new page
            if (currentY > 250) {
                doc.addPage();
                currentY = 20;
            }

            currentY = addSectionTitle('Prepayment Savings', currentY);

            // Calculate savings (simplified logic for export display)
            // Note: Ideally we would pass the full prepayment calculation results
            // For now we show the input
            const prepaymentInfo = [
                ['Extra Monthly Payment', formatCurrency(prepaymentData.extraPayment)],
                ['Note', 'See full application for detailed savings analysis']
            ];

            doc.autoTable({
                startY: currentY,
                head: [['Item', 'Value']],
                body: prepaymentInfo,
                theme: 'grid',
                headStyles: { fillColor: [72, 187, 120] }
            });
        }

        // Loan Comparisons (if any)
        if (comparisons && comparisons.length > 0) {
            doc.addPage();
            currentY = 20;
            currentY = addSectionTitle('Loan Comparisons', currentY);

            const comparisonData = comparisons.map((comp, index) => [
                `Scenario ${index + 1}`,
                formatCurrency(comp.homePrice),
                formatPercent(comp.interestRate),
                formatCurrency(comp.monthlyPayment),
                formatCurrency(comp.totalInterest),
                formatCurrency(comp.totalCost)
            ]);

            // Add current loan as baseline
            comparisonData.unshift([
                'Current Loan',
                formatCurrency(loanData.homePrice),
                formatPercent(loanData.interestRate),
                formatCurrency(calculatedData.totalMonthly),
                // Note: Total interest/cost would need to be calculated from schedule sum
                'See Schedule',
                'See Schedule'
            ]);

            doc.autoTable({
                startY: currentY,
                head: [['Scenario', 'Price', 'Rate', 'Monthly', 'Total Interest', 'Total Cost']],
                body: comparisonData,
                theme: 'grid',
                headStyles: { fillColor: [237, 137, 54] }
            });
        }

        // Amortization Schedule (first year)
        doc.addPage();
        doc.setFontSize(14);
        doc.text('Amortization Schedule (First Year)', 14, 20);

        const firstYearSchedule = calculatedData.amortizationSchedule
            .filter(payment => payment.year === 1)
            .map(payment => [
                payment.month,
                formatCurrency(payment.payment),
                formatCurrency(payment.principal),
                formatCurrency(payment.interest),
                formatCurrency(payment.balance)
            ]);

        doc.autoTable({
            startY: 25,
            head: [['Month', 'Payment', 'Principal', 'Interest', 'Balance']],
            body: firstYearSchedule,
            theme: 'grid',
            headStyles: { fillColor: [102, 126, 234] },
            styles: { fontSize: 8 }
        });

        // Disclaimer
        doc.addPage();
        doc.setFontSize(12);
        doc.text('Disclaimer', 14, 20);
        doc.setFontSize(9);
        const disclaimer = doc.splitTextToSize(
            'This calculator is provided for educational and informational purposes only. The calculations and estimates ' +
            'provided are based on the information you input and certain assumptions about property taxes, insurance rates, ' +
            'and PMI. Actual costs may vary. This tool should not be considered as financial, legal, or tax advice. ' +
            'Please consult with qualified financial, legal, and tax professionals before making any decisions regarding ' +
            'home financing. Interest rates, property taxes, insurance premiums, and other costs are subject to change. ' +
            'For ARM loans, rates may adjust after the initial fixed period, which could significantly impact your monthly payments.',
            180
        );
        doc.text(disclaimer, 14, 30);

        // Save the PDF
        doc.save(`principal-saver-loan-analysis-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
        console.error('exportToPDF error:', error);
    }
}

/**
 * Export amortization schedule to Excel
 * @param {object} loanData - Complete loan data
 * @param {object} calculatedData - Calculated results
 * @param {object} prepaymentData - Prepayment configuration
 * @param {array} comparisons - List of loan comparisons
 * @param {object} manualOverrides - Manual override values
 */
export function exportToExcel(loanData, calculatedData, prepaymentData, comparisons, manualOverrides) {
    try {
        const workbook = XLSX.utils.book_new();

        // Loan Summary Sheet
        const summaryData = [
            ['Principal Saver - Loan Analysis'],
            ['Generated on', new Date().toLocaleDateString()],
            [''],
            ['Loan Summary'],
            ['Home Price', loanData.homePrice],
            ['Down Payment', loanData.downPayment],
            ['Down Payment %', loanData.downPaymentPercent + '%'],
            ['Loan Amount', calculatedData.loanAmount],
            ['Interest Rate', loanData.interestRate + '%'],
            ['Loan Type', loanData.loanType],
            ['Buydown Option', loanData.buydownType || 'None'],
            ['Zip Code', loanData.zipCode],
            [''],
            ['Monthly Payment Breakdown'],
            ['Principal & Interest', calculatedData.monthlyPI],
            ['Property Tax', calculatedData.escrowDetails.propertyTaxDetails.amount],
            ['Homeowner Insurance', calculatedData.escrowDetails.insuranceDetails.amount],
            ['PMI', calculatedData.autoValues.pmi],
            ['Total Escrow', calculatedData.escrow],
            ['Total Monthly Payment', calculatedData.totalMonthly],
            [''],
            ['Manual Overrides'],
            ['Property Tax', manualOverrides.propertyTax ? 'Yes' : 'No'],
            ['Insurance', manualOverrides.insurance ? 'Yes' : 'No'],
            ['PMI', manualOverrides.pmi ? 'Yes' : 'No']
        ];

        if (prepaymentData && prepaymentData.extraPayment > 0) {
            summaryData.push(['']);
            summaryData.push(['Prepayment Settings']);
            summaryData.push(['Extra Monthly Payment', prepaymentData.extraPayment]);
        }

        const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(workbook, summarySheet, 'Loan Summary');

        // Comparisons Sheet (if any)
        if (comparisons && comparisons.length > 0) {
            const compRows = [
                ['Scenario', 'Home Price', 'Interest Rate', 'Monthly Payment', 'Total Interest', 'Total Cost']
            ];

            comparisons.forEach((comp, index) => {
                compRows.push([
                    `Scenario ${index + 1}`,
                    comp.homePrice,
                    comp.interestRate + '%',
                    comp.monthlyPayment,
                    comp.totalInterest,
                    comp.totalCost
                ]);
            });

            const compSheet = XLSX.utils.aoa_to_sheet(compRows);
            XLSX.utils.book_append_sheet(workbook, compSheet, 'Comparisons');
        }

        // Amortization Schedule Sheet
        const scheduleData = [
            ['Month', 'Year', 'Payment', 'Principal', 'Interest', 'Balance', 'Total Interest Paid', 'Total Principal Paid']
        ];

        let totalInterest = 0;
        let totalPrincipal = 0;

        calculatedData.amortizationSchedule.forEach(payment => {
            totalInterest += payment.interest;
            totalPrincipal += payment.principal;

            scheduleData.push([
                payment.month,
                payment.year,
                payment.payment,
                payment.principal,
                payment.interest,
                payment.balance,
                totalInterest,
                totalPrincipal
            ]);
        });

        const scheduleSheet = XLSX.utils.aoa_to_sheet(scheduleData);

        // Set column widths
        scheduleSheet['!cols'] = [
            { wch: 8 },  // Month
            { wch: 6 },  // Year
            { wch: 12 }, // Payment
            { wch: 12 }, // Principal
            { wch: 12 }, // Interest
            { wch: 14 }, // Balance
            { wch: 18 }, // Total Interest
            { wch: 18 }  // Total Principal
        ];

        XLSX.utils.book_append_sheet(workbook, scheduleSheet, 'Amortization Schedule');

        // Save the Excel file
        XLSX.writeFile(workbook, `principal-saver-loan-analysis-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
        console.error('exportToExcel error:', error);
    }
}

/**
 * Prepare page for printing
 */
export function preparePrint() {
    window.print();
}
